import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import Layout from '../../components/global/layout';
import shared from './shared';
import methodModel from '../../methods/methods';
import { CgArrowLeft, CgFormatCenter } from 'react-icons/cg';
import { LiaEdit } from 'react-icons/lia';
import { useSelector } from 'react-redux';
import loader from '../../methods/loader';
import ApiClient from '../../methods/api/apiClient';
import { FaChevronLeft, FaEdit } from 'react-icons/fa';
import { RiFileList3Line } from 'react-icons/ri';
import { IoLocation } from 'react-icons/io5';
import { IoIosHourglass, IoMdImages } from 'react-icons/io';
import { TbArrowsExchange, TbBuildingStore, TbCoins, TbCurrencyPound, TbPercentage } from 'react-icons/tb';
import { MdOutlinePlace, MdOutlineQrCode2 } from 'react-icons/md';
import { LuDownload, LuPrinter, LuCopy, LuCheck, LuEye, LuEyeOff, LuShieldCheck } from 'react-icons/lu';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// ── Reusable field row ──────────────────────────────────────────────────────
const Field = ({ label, value, children }) => (
  <div className="flex flex-col">
    <label className="text-[12px] text-[#0000009c] tracking-wider uppercase mb-1">{label}</label>
    {children || (
      <p className="text-[13px] text-black bg-[#f5f5f5] min-h-[38px] rounded-md px-3 py-2">
        {value || 'N/A'}
      </p>
    )}
  </div>
);

// ── Section wrapper ─────────────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="h-full bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border">
    <div className="">{children}</div>
  </div>
);

// ── Approval Status Badge ───────────────────────────────────────────────────
const approvalBadgeConfig = {
  approved: {
    className: 'bg-[#CC99001f] text-[#CC9900] border border-[#fddd86]',
    dot: 'bg-[#CC9900]',
    label: 'Approved',
  },
  pending: {
    className: 'bg-yellow-50 text-yellow-600 border border-yellow-200',
    dot: 'bg-yellow-400',
    label: 'Pending',
  },
  rejected: {
    className: 'bg-red-50 text-red-500 border border-red-200',
    dot: 'bg-red-500',
    label: 'Rejected',
  },
};

function InputField({
  label,
  sublabel,
  icon: Icon,
  prefix,
  suffix,
  name,
  value,
  onChange,
  min,
  max,
  step = 1,
  error,
  hint,
  placeholder = '0',
}) {
  return (
    <div className="mb-1">
      <label className="mb-1.5 flex items-center gap-1.5 flex-wrap text-sm font-medium text-gray-700">
        {Icon && <Icon size={14} className="text-[#CC9900]" />}
        {label}
        {sublabel && (
          <span className="ml-1 text-[11px] font-normal text-gray-400">{sublabel}</span>
        )}
      </label>

      <div className="relative flex items-center">
        {prefix && (
          <span className="absolute left-3 text-sm font-medium text-gray-400 pointer-events-none select-none">
            {prefix}
          </span>
        )}
        <input
          type="number"
          name={name}
          value={value}
          min={min}
          disabled
          max={max}
          step={step}
          placeholder={placeholder}
          onChange={(e) => onChange(name, e.target.value)}
          className={[
            'border bg-white w-full text-sm placeholder:text-gray-400 rounded-lg h-10',
            'flex items-center overflow-hidden',
            '!ring-[#CC9900] !outline-[#CC9900]',
            'transition-colors duration-150',
            prefix ? 'pl-8 pr-3' : suffix ? 'pl-3 pr-8' : 'px-3',
            error
              ? 'border-red-400 focus:border-red-500'
              : 'border-gray-200 focus:border-[#CC9900]',
          ].join(' ')}
        />
        {suffix && (
          <span className="absolute right-3 text-sm font-medium text-gray-400 pointer-events-none select-none">
            {suffix}
          </span>
        )}
      </div>

      {hint && !error && (
        <p className="mt-1 text-[11px] text-gray-400 leading-relaxed">{hint}</p>
      )}
    </div>
  );
}

function PreviewPill({ points, rate }) {
  const p = Number(points) || 0;
  const r = Number(rate) || 0;
  if (!p || !r) return null;
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#CC990012] border border-[#ffe295] text-xs font-medium text-[#CC9900]">
      <TbCoins size={13} />
      1 pts = £{parseFloat(r / p).toFixed(1)}
    </div>
  );
}

const ApprovalBadge = ({ status }) => {
  const config = approvalBadgeConfig[status] || {
    className: 'bg-gray-100 text-gray-500 border border-gray-200',
    dot: 'bg-gray-400',
    label: status || 'N/A',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full capitalize ${config.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

// ── Per-location Hours display ──────────────────────────────────────────────
const HoursDisplay = ({ hours }) => (
  <div>
    {DAYS.map((day) => {
      const val = hours?.[day];
      const closed = val === null || val === undefined;
      return (
        <div key={day} className="flex justify-between items-center gap-3 py-2 border-b border-gray-50 last:border-0">
          <span className="min-w-[78px] w-[78px] text-sm font-medium text-gray-600 capitalize flex-shrink-0">{day}</span>
          {closed ? (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-red-50 text-[#FF0000] border border-red-100 italic">
              Closed
            </span>
          ) : (
            <span className="text-sm">
              {val || <span className="text-gray-400 italic">Not set</span>}
            </span>
          )}
        </div>
      );
    })}
  </div>
);

// ── QR Download helper ──────────────────────────────────────────────────────
const handleDownloadQR = (qrURL, stationName) => {
  const link = document.createElement('a');
  link.href = qrURL;
  link.download = `${stationName || 'station'}-qr.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ── QR Print helper ─────────────────────────────────────────────────────────
const handlePrintQR = (qrURL, stationName) => {
  const printWindow = window.open('', '_blank', 'width=420,height=540');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>QR Code — ${stationName || 'Station'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            background: #fff;
            padding: 32px;
          }
          .label {
            font-size: 18px;
            font-weight: 700;
            color: #2D2D2D;
            margin-bottom: 20px;
            text-align: center;
          }
          img {
            width: 240px;
            height: 240px;
            object-fit: contain;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 12px;
          }
          .footer {
            margin-top: 16px;
            font-size: 12px;
            color: #9ca3af;
          }
        </style>
      </head>
      <body>
        <p class="label">${stationName || 'Station'}</p>
        <img src="${qrURL}" alt="QR Code" />
        <p class="footer">Scan to access this station</p>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); window.close(); }, 300);
          };
        <\/script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

// ── Single station card with QR ─────────────────────────────────────────────
const StationCard = ({ station, index }) => {
  const hasQR = !!station.qrURL;

  return (
    <div className="bg-[#fafafa] border border-gray-100 rounded-[10px] overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-gray-100">
        <span className="w-5 h-5 rounded-full bg-[#CC99001f] text-[#CC9900] text-[10px] font-bold flex items-center justify-center flex-shrink-0">
          {index + 1}
        </span>
        <span className="text-sm font-semibold text-[#2D2D2D] flex-1 truncate">
          {station.name || 'Unnamed Station'}
        </span>
        {hasQR && (
          <MdOutlineQrCode2 className="text-[#CC9900] text-base flex-shrink-0" />
        )}
      </div>

      {/* QR section */}
      {hasQR ? (
        <div className="flex flex-col items-center gap-3 p-4">
          {/* QR image */}
          <div className="bg-white rounded-[8px] border border-gray-200 p-2 shadow-sm">
            <img
              src={station.qrURL}
              alt={`QR for ${station.name}`}
              className="w-[140px] h-[140px] object-contain"
            />
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-2 w-full">
            <button
              type="button"
              onClick={() => handleDownloadQR(station.qrURL, station.name)}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-[#CC9900] bg-[#CC99001f] hover:bg-[#CC990030] border border-[#ffe295] px-3 py-2 rounded-lg transition"
            >
              <LuDownload size={13} />
              Download
            </button>
            <button
              type="button"
              onClick={() => handlePrintQR(station.qrURL, station.name)}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg transition"
            >
              <LuPrinter size={13} />
              Print
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-1.5 py-6 px-4 text-center">
          <MdOutlineQrCode2 className="text-gray-200 text-4xl" />
          <p className="text-xs text-gray-400 italic">No QR code available</p>
        </div>
      )}
    </div>
  );
};

// ── Security PIN badge ──────────────────────────────────────────────────────
const SecurityPin = ({ pin }) => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!pin) return null;

  const masked = '● ● ● ●';
  const shown = String(pin).split('').join('  ');

  const handleCopy = () => {
    navigator.clipboard.writeText(String(pin)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-[#CC990012] border border-[#ffe295] shadow-sm">
      {/* Shield icon */}
      <div className="w-8 h-8 rounded-lg bg-white border border-[#ffe295] flex items-center justify-center flex-shrink-0 shadow-sm">
        <LuShieldCheck size={15} className="text-[#CC9900]" />
      </div>

      {/* Label + PIN */}
      <div className="flex flex-col">
        <span className="text-[9px] uppercase tracking-widest text-[#CC9900] font-semibold leading-none mb-1.5">
          Security PIN
        </span>
        <span
          className="font-mono text-base font-bold text-[#2D2D2D] select-none"
          style={{ letterSpacing: visible ? '0.22em' : '0.14em' }}
        >
          {visible ? shown : masked}
        </span>
      </div>

      {/* Divider */}
      <div className="w-px h-8 bg-[#ffe295] mx-1" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        {/* Toggle visibility */}
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          title={visible ? 'Hide PIN' : 'Show PIN'}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-[#CC9900] hover:bg-[#CC990020] transition"
        >
          {visible ? <LuEyeOff size={14} /> : <LuEye size={14} />}
        </button>

        {/* Copy */}
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy PIN'}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition ${copied
            ? 'text-[#16a34a] bg-[#16a34a12]'
            : 'text-[#CC9900] hover:bg-[#CC990020]'
            }`}
        >
          {copied ? <LuCheck size={14} /> : <LuCopy size={14} />}
        </button>
      </div>
    </div>
  );
};

// ── Stations grid ───────────────────────────────────────────────────────────
const StationsDisplay = ({ stations }) => {
  if (!stations || stations.length === 0) {
    return <p className="text-sm text-gray-400 italic">No stations added.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3">
      {stations.map((station, i) => (
        <StationCard key={i} station={station} index={i} />
      ))}
    </div>
  );
};

function ViewVenueManagement() {
  const user = useSelector((state) => state.user);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (!id) return;
    loader(true);
    ApiClient.get(shared.detailApi, { id }).then((res) => {
      if (res.success) {
        console.log(res.data, 'res.data');
        setData(res.data);
      } else {
        navigate(-1);
      }
      loader(false);
    });
  }, [id]);

  if (!data) return <Layout><div className="p-8 text-gray-400 text-sm">Loading...</div></Layout>;

  // ── Normalize locations: support both new array and old flat fields ──────
  const locations = (() => {
    if (data.locations && data.locations.length > 0) return data.locations;
    if (data.address || data.city) {
      return [{
        address: data.address,
        city: data.city,
        state: data.state,
        country: data.country,
        pinCode: data.pinCode,
        currentLocation: data.currentLocation,
        hours: data.hours,
        stations: data.stations || [],
      }];
    }
    return [];
  })();

  return (
    <Layout>
      <div className="wrapper_section">

        {/* ── Header ── */}
        <div className='flex gap-3 justify-between flex-wrap mb-8'>
          <div className="flex gap-3">
            <Tooltip placement="top" title="Back">
              <Link
                onClick={() => navigate(-1)}
                className="min-w-[30px] w-[30px] h-[30px] rounded-full bg-[#CC9900] text-white flex items-center justify-center hover:opacity-80"
              >
                <FaChevronLeft />
              </Link>
            </Tooltip>
            <div className=''>
              <h3 className="text-lg lg:text-2xl font-semibold text-[#221E10]">
                {data?.name || 'Venue'} Details
              </h3>
              <p className='text-sm text-[#2D2D2D]'></p>
            </div>
          </div>

          {methodModel.hasPermission(user, shared.permissionKey, 'edit') && (
            <Tooltip placement="top" title="Edit">
              <Link to={`/${shared.url}/edit/${id}`} className=" flex items-center gap-1 hover:opacity-80 ml-auto">
                <span className='cursor-pointer rounded-full w-8 h-8 flex items-center justify-center text-lg hover:opacity-80 bg-[#F2F2F2] text-[#797979]'><FaEdit className='' /></span>
                <p>Edit</p>
              </Link>
            </Tooltip>
          )}
        </div>

        {/* ── Rejection Banner ── */}
        {data?.approvalStatus === 'rejected' && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-600 mb-0.5">This venue has been rejected</p>
              <p className="text-sm text-red-500">
                {data?.reason || 'No reason provided.'}
              </p>
            </div>
          </div>
        )}

        <div className='mt-[6rem]'>
          <div className='grid grid-cols-12 gap-6'>

            {/* ── Profile Card ── */}
            <div className='col-span-12'>
              <div className='h-full bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
                <div className="flex max-lg:flex-wrap gap-6">

                  <div className="relative w-[340px] flex flex-col items-center justify-center gap-3 mx-auto pt-[3.5rem]">
                    {data?.logo ? (
                      <img
                        src={methodModel.noImg(data.logo, 'venue')}
                        className="bg-white w-[130px] h-[130px] rounded-full object-contain shadow absolute top-[-90px]"
                        alt="logo"
                      />
                    ) : (
                      <div className="w-[130px] h-[130px] rounded-full bg-[#fffaed] flex items-center justify-center shadow absolute top-[-90px]">
                        <span className="text-[#CC9900] text-4xl font-bold uppercase">
                          {data?.name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div className='text-center space-y-1'>
                      <h2 className='text-lg text-[#2D2D2D] font-[600]'>{data?.name}</h2>
                      <p className='text-sm text-[#B3B3B3]'>{data?.email}</p>
                      <p className='text-sm text-[#B3B3B3]'>Mobile : {data?.dialCode ? `${data.dialCode} ` : ''}{data?.mobileNo || 'N/A'}</p>
                      <p className='text-sm text-[#B3B3B3]'>Telephone : {data?.landlineNo || 'N/A'}</p>
                      <p className='text-sm text-[#B3B3B3]'>Type : {data?.type || 'N/A'}</p>
                      <p className='text-sm text-[#B3B3B3]'>Allow Customer to use points Globally : {data?.allowGlobalPoints ? 'Yes' : 'No'}</p>
                      <p className='text-sm text-[#B3B3B3]'>Reward System implemented : {data?.isRewardAllowed ? 'Yes' : 'No'}</p>
                    </div>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className={`min-w-[90px] text-center text-xs font-medium px-3 py-1 rounded-full capitalize
                        ${data.status === 'active'
                          ? 'bg-[#E5F9E6] text-[#00BF06] border border-[#ADE6AF]'
                          : 'bg-[#ffefef] text-[#ff3b3b] border border-[#ffa6a6]'}`}>
                        {data.status === 'inactive' ? 'Inactive' : data.status || 'N/A'}
                      </span>
                      {data?.approvalStatus && (
                        <ApprovalBadge status={data.approvalStatus} />
                      )}
                    </div>
                  </div>

                  <div className='w-full space-y-3'>
                    <div className='flex gap-2'>
                      <label className='text-sm text-[#CC9900] font-[600]'>Website :</label>
                      <p className="text-sm text-[#2D2D2D]">
                        {data?.website
                          ? <a href={data.website} target="_blank" rel="noopener noreferrer" className="hover:underline break-all">{data.website}</a>
                          : 'N/A'}
                      </p>
                    </div>
                    <div className=''>
                      <label className='text-sm text-[#CC9900] font-[600]'>Description</label>
                      <p className="text-sm text-[#2D2D2D]">{data?.description}</p>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                LOCATIONS — one expandable card per location
            ══════════════════════════════════════════════════════════════ */}
            {locations.length > 0 && (
              <div className='col-span-12'>
                <div className="flex items-center gap-2 mb-4">
                  <IoLocation className="text-[#CC9900] text-xl" />
                  <h4 className="text-[#2D2D2D] font-semibold text-base">
                    Locations
                    <span className="ml-2 text-xs font-normal text-gray-400">({locations.length})</span>
                  </h4>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {locations.map((loc, locIndex) => {
                    const locParts = [loc.address, loc.city, loc.state, loc.pinCode, loc.country].filter(Boolean);
                    const coords = loc.currentLocation?.coordinates;
                    const lng = coords?.[0];
                    const lat = coords?.[1];
                    const hasStations = loc.stations && loc.stations.length > 0;

                    return (
                      <div
                        key={locIndex}
                        className="bg-white rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border overflow-hidden"
                      >
                        {/* Location card header */}
                        <div className="flex items-center gap-2 px-5 py-3 bg-[#faf7ed] border-b">
                          <MdOutlinePlace className="text-[#CC9900] text-lg" />
                          <span className="font-semibold text-[#2D2D2D] text-sm">
                            Location {locIndex + 1}
                            {loc.city ? ` — ${loc.city}` : ''}
                          </span>
                          {hasStations && (
                            <span className="ml-auto text-xs text-gray-400 font-normal">
                              {loc.stations.length} station{loc.stations.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        <div className="p-5 grid grid-cols-1 gap-6">

                          {/* ── Address fields ── */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <IoLocation className="text-[#CC9900] text-sm" />
                              <h5 className="text-[#2D2D2D] font-semibold text-sm">Address</h5>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                              <div className="col-span-12">
                                <Field label="Full Address" value={locParts.join(', ') || undefined} />
                              </div>
                              <div className="col-span-6 sm:col-span-3">
                                <Field label="City" value={loc.city} />
                              </div>
                              <div className="col-span-6 sm:col-span-3">
                                <Field label="State" value={loc.state} />
                              </div>
                              <div className="col-span-6 sm:col-span-3">
                                <Field label="Country" value={loc.country} />
                              </div>
                              <div className="col-span-6 sm:col-span-3">
                                <Field label="Pin Code" value={loc.pinCode} />
                              </div>
                              {lat && (
                                <div className="col-span-6 sm:col-span-3">
                                  <Field label="Latitude" value={lat?.toString()} />
                                </div>
                              )}
                              {lng && (
                                <div className="col-span-6 sm:col-span-3">
                                  <Field label="Longitude" value={lng?.toString()} />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ── Security PIN ── */}
                          {loc.securityPin && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <LuShieldCheck className="text-[#CC9900] text-sm" />
                                <h5 className="text-[#2D2D2D] font-semibold text-sm">Security PIN</h5>
                              </div>
                              <SecurityPin pin={loc.securityPin} />
                            </div>
                          )}

                          <hr className="border-gray-100" />

                          {/* ── Operating Hours ── */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <IoIosHourglass className="text-[#CC9900] text-sm" />
                              <h5 className="text-[#2D2D2D] font-semibold text-sm">Operating Hours</h5>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                              <HoursDisplay hours={loc.hours} />
                            </div>
                          </div>

                          {/* ── Stations with QR ── */}
                          {hasStations && (
                            <>
                              <hr className="border-gray-100" />
                              <div>
                                <div className="flex items-center gap-2 mb-4">
                                  <TbBuildingStore className="text-[#CC9900] text-sm" />
                                  <h5 className="text-[#2D2D2D] font-semibold text-sm">
                                    Stations
                                    <span className="ml-1.5 text-xs font-normal text-gray-400">
                                      ({loc.stations.length})
                                    </span>
                                  </h5>
                                </div>
                                <StationsDisplay stations={loc.stations} />
                              </div>
                            </>
                          )}

                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Map card (first location) ── */}
            {/* {locations.length > 0 && (
              <div className='col-span-12 lg:col-span-5'>
                <div className='h-full bg-white rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
                  <img src='/img/map-img.png' className='w-full h-[180px] object-cover rounded-t-[12px]' />
                  <div className='space-y-1 p-5'>
                    <p className='text-sm text-[#2D2D2D]'>{locations[0]?.country}</p>
                    <h2 className="text-xl text-[#CC9900] font-[600]">{locations[0]?.city}</h2>
                    <p className='text-sm text-[#2D2D2D]'>{locations[0]?.state}</p>
                    <p className='text-sm text-[#2D2D2D]'>{locations[0]?.pinCode}</p>
                  </div>
                </div>
              </div>
            )} */}

            {/* ── Amenities ── */}
            {data?.amenities?.length > 0 && (
              <div className={`col-span-12 ${locations.length > 0 ? 'lg:col-span-12' : ''}`}>
                <Section>
                  <div className='mb-5'>
                    <div className='flex gap-2'>
                      <CgFormatCenter className="text-[#CC9900] text-xl relative top-[1px]" />
                      <h4 className='text-[#2D2D2D] font-semibold'>Amenities</h4>
                    </div>
                    <p className='text-sm text-[#717182]'></p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.amenities.map((a) => (
                      <span
                        key={a}
                        className="text-xs font-medium px-3 py-1 rounded-full bg-[#CC990012] text-[#CC9900] border border-[#ffe295]"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </Section>
              </div>
            )}

            {/* ── Venue Images ── */}
            {data?.images?.length > 0 && (
              <div className='col-span-12'>
                <Section>
                  <div className='mb-5'>
                    <div className='flex gap-2'>
                      <IoMdImages className="text-[#CC9900] text-xl relative top-[1px]" />
                      <h4 className='text-[#2D2D2D] font-semibold'>Venue Images</h4>
                    </div>
                    <p className='text-sm text-[#717182]'></p>
                  </div>
                  <div className="max-[480px]:grid max-[480px]:grid-cols-2 min-[480px]:flex min-[480px]:flex-wrap gap-3">
                    {data.images.map((img, i) => (
                      <Tooltip key={i} title="Click to open">
                        <img
                          src={methodModel.noImg(img, 'venue')}
                          className="max-[480px]:w-full w-[160px] max-[480px]:h-[90px] h-[110px] object-cover rounded-lg border-2 border-white shadow cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => window.open(methodModel.noImg(img, 'venue'), '_blank')}
                          alt={`venue-${i + 1}`}
                        />
                      </Tooltip>
                    ))}
                  </div>
                </Section>
              </div>
            )}

            {/* ── Exchange Rate & Redemption Limits ── */}
            <div className='col-span-12'>
              <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
                <Section
                  title="Exchange rate"
                  description="Define how many points equal 1 pound sterling."
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <InputField
                      label="Points"
                      icon={TbCoins}
                      name="defaultPoints"
                      value={data?.defaultPoints || 0}
                      onChange={() => { }}
                      min={1}
                      placeholder="e.g. 5"
                      hint="Number of points the customer earns or spends."
                      error={null}
                    />
                    <InputField
                      label="Pound value"
                      sublabel="(exchange rate)"
                      icon={TbCurrencyPound}
                      prefix="£"
                      name="exchange_rate"
                      value={data?.exchange_rate || 0}
                      onChange={() => { }}
                      placeholder="e.g. 1"
                      hint="Pound amount these points are worth."
                      error={null}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <TbArrowsExchange size={14} className="text-gray-300" />
                    <span className="text-[12px] text-gray-400">Live preview:</span>
                    <PreviewPill points={data.defaultPoints} rate={data.exchange_rate} />
                    {(!data.defaultPoints || !data.exchange_rate) && (
                      <span className="text-[12px] text-gray-300 italic">fill both fields to preview</span>
                    )}
                  </div>
                </Section>

                <Section
                  title="Redemption limits"
                  description="Set the minimum and maximum percentage of an order a customer can pay using points."
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField
                      label="Min redemption"
                      icon={TbPercentage}
                      suffix="%"
                      name="min_redemption_percentage"
                      value={data?.min_redemption_percentage || 0}
                      onChange={() => { }}
                      min={0}
                      max={100}
                      placeholder="e.g. 5"
                      hint="Minimum % of order payable with points."
                      error={null}
                    />
                    <InputField
                      label="Max redemption"
                      icon={TbPercentage}
                      suffix="%"
                      name="max_redemption_percentage"
                      value={data?.max_redemption_percentage || 0}
                      onChange={() => { }}
                      min={1}
                      max={100}
                      placeholder="e.g. 50"
                      hint="Maximum % of order payable with points."
                      error={null}
                    />
                  </div>
                  {data.min_redemption_percentage !== '' && data.max_redemption_percentage !== '' && (
                    <div className="mt-4 px-1">
                      <div className="flex justify-between text-[11px] text-gray-400 mb-1">
                        <span>0%</span>
                        <span>100%</span>
                      </div>
                      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="absolute h-full bg-[#CC9900] rounded-full transition-all duration-300"
                          style={{
                            left: `${Math.min(Math.max(Number(data.min_redemption_percentage), 0), 100)}%`,
                            width: `${Math.max(
                              Math.min(Number(data.max_redemption_percentage), 100) -
                              Math.min(Math.max(Number(data.min_redemption_percentage), 0), 100),
                              0
                            )}%`,
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-[11px] font-medium text-[#CC9900] mt-1">
                        <span>{data.min_redemption_percentage}% min</span>
                        <span>{data.max_redemption_percentage}% max</span>
                      </div>
                    </div>
                  )}
                </Section>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ViewVenueManagement;